import { useSyncExternalStore } from 'react';

/**
 * `matchMedia` as an external store: the value comes from the browser, changes on its
 * own (rotation, resize, the WebView keyboard shrinking the viewport) and belongs to no
 * component — subscribe/snapshot is the exact fit, without the double render of
 * `useState` + `useEffect`, and the snapshot is re-read on subscribe, so a viewport
 * change between render and commit is never lost.
 *
 * Pass a query built from `breakpoints.ts` (`atLeast('wide')`), not a literal.
 *
 * `false` when the answer is unknown: on the server, on the first hydrated render, and
 * in hosts without `matchMedia` (old embedded WebViews). Every consumer treats "don't
 * know" as the presentation that does not depend on the size — narrow for the shell,
 * wide for the deterministic surfaces.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof globalThis.matchMedia !== 'function') return () => {};
      const list = globalThis.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => {
        list.removeEventListener('change', onChange);
      };
    },
    () => typeof globalThis.matchMedia === 'function' && globalThis.matchMedia(query).matches,
    () => false,
  );
}
